import { toPng } from 'html-to-image';
import { buildImageProxyUrl, shouldProxyImageUrl } from './imageProxyUrl';

/** 1×1 şeffaf PNG — son çare */
const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

function resolveBackgroundColor(element: HTMLElement): string {
  let node: HTMLElement | null = element;
  while (node) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
      return bg;
    }
    node = node.parentElement;
  }
  return '#ffffff';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function waitForImage(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Image failed to load'));
    };
    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  });
}

async function fetchBlob(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-cache' });
    if (!res.ok) return null;
    return res.blob();
  } catch {
    return null;
  }
}

async function fetchImageAsDataUrl(url: string): Promise<string | null> {
  if (shouldProxyImageUrl(url)) {
    const proxied = await fetchBlob(buildImageProxyUrl(url));
    if (proxied) {
      return blobToDataUrl(proxied);
    }
  }

  const direct = await fetchBlob(url);
  if (direct) {
    return blobToDataUrl(direct);
  }

  return null;
}

/** Karttaki img öğelerini yakalamadan önce data URL'ye çevirir (logolar dahil). */
async function inlineCardImages(root: HTMLElement): Promise<() => void> {
  const restores: Array<() => void> = [];
  const images = Array.from(root.querySelectorAll('img'));
  const waiters: Promise<void>[] = [];

  for (const img of images) {
    const src = img.currentSrc || img.src;
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) continue;

    const originalSrc = img.src;
    const originalCrossOrigin = img.crossOrigin;

    const inlined = await fetchImageAsDataUrl(src);
    if (!inlined) continue;

    img.crossOrigin = 'anonymous';
    img.src = inlined;
    img.removeAttribute('srcset');
    waiters.push(waitForImage(img));

    restores.push(() => {
      img.src = originalSrc;
      img.crossOrigin = originalCrossOrigin;
    });
  }

  await Promise.all(waiters);

  return () => restores.forEach((r) => r());
}

const captureOptions = (element: HTMLElement, extra?: Parameters<typeof toPng>[1]) => ({
  cacheBust: true,
  pixelRatio: 1,
  backgroundColor: resolveBackgroundColor(element),
  skipFonts: true,
  imagePlaceholder: TRANSPARENT_PIXEL,
  fetchRequestInit: { mode: 'cors' as RequestMode, credentials: 'omit' as RequestCredentials },
  onImageErrorHandler: () => {
    /* inlineCardImages sonrası kalan hatalarda yakalamayı durdurma */
  },
  ...extra,
});

export async function downloadElementAsPng(
  element: HTMLElement,
  filenameBase: string
): Promise<void> {
  const restoreImages = await inlineCardImages(element);

  try {
    const dataUrl = await toPng(element, captureOptions(element));

    if (!dataUrl?.startsWith('data:image')) {
      throw new Error('Invalid capture result');
    }

    const link = document.createElement('a');
    link.download = `${filenameBase}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    restoreImages();
  }
}
